import { ctrylangCombinations, languageCodes, multiLingualCountries } from "../../constants/Country";
import { LOCALSESSIONDATAKEYS, USER_LANGUAGE_CODE_TYPE } from "../../constants/AppConstants";
import { getLocalSessionData, removeLocalSessionData, setLocalSessionData } from "../Authentication/handleUserSession";
import { ENVSettings    } from "../../constants/ENVSettings";

/** Result of the first resolveAppLanguage() call; later calls reuse it and write nothing. */
let resolvedLanguage = null;

/** Domains (Finland, Sweden, Denmark) whose display language is always the domain language, never the browser's. */
const DOMAIN_ONLY_DISPLAY_COUNTRIES = ["fi", "se", "dk"];

/**
 * Display language chosen by a launch URL that carried a `language` query parameter (MobileConnect).
 * Kept for the browser tab so page loads without a query string (B2C return, redirects, refresh)
 * keep that language. sessionStorage: it ends with the tab and never leaks into a later web visit.
 */
const LAUNCH_DISPLAY_LOCALE_KEY = "ECOPRO_LAUNCH_DISPLAY_LOCALE";

const readLaunchDisplayLocale = () => {
  try {
    return sessionStorage.getItem(LAUNCH_DISPLAY_LOCALE_KEY);
  } catch (e) {
    return null;
  }
};

const saveLaunchDisplayLocale = (locale) => {
  try {
    sessionStorage.setItem(LAUNCH_DISPLAY_LOCALE_KEY, locale);
  } catch (e) {
    // storage unavailable: the language still applies to this page load
  }
};

const getLangPrefix = (locale) => (typeof locale === "string" ? locale.split("-")[0].toLowerCase() : "");

/**
 * Multilingual domains (BE, CH): takes the language part of each preference, in order,
 * and returns the first one the market supports, joined with the domain country.
 * No supported language found -> domain default.
 * e.g. BE: ["en-US", "fr-FR"] -> "fr-BE",  ["fr"] -> "fr-BE",  ["en-US"] -> "nl-BE"
 */
const matchMultiLingualLocale = (preferences, ctryCode, domainDefault) => {
  const supportedPrefixes = multiLingualCountries[ctryCode] || [];
  const matchedPrefix = (preferences || [])
    .filter(Boolean)
    .map(getLangPrefix)
    .find(prefix => supportedPrefixes.includes(prefix));
  return matchedPrefix ? `${matchedPrefix}-${ctryCode}` : domainDefault;
};

/** Multilingual resolver (BE, CH) */
const resolveMultiLingual = (ctryCode, domainDefault, qsLanguage, isQSApplicable, browserLanguage) => {
  const browserPreferences = (navigator.languages && navigator.languages.length > 0)
    ? navigator.languages
    : [browserLanguage];
  const preferences = isQSApplicable ? [qsLanguage] : browserPreferences.filter(Boolean);
  return matchMultiLingualLocale(preferences, ctryCode, domainDefault);
};

/** Single-language domain resolver (UK, DE, FR, IT, ES, Nordic, etc.) - strictly enforces domain default */
const resolveSingleDomain = (domainDefault) => domainDefault;

/** Locales that can be shown as the UI language: every locale the application maps to a country. */
const eligibleDisplayLocales = [...new Set([
  ...Object.values(languageCodes),
  ...Object.values(ctrylangCombinations).flat()
])];

/** "fr-be" / "FR-BE" -> "fr-BE"; anything without a region -> "" */
const normalizeRegionalLocale = (locale) => {
  const [language, region] = String(locale).split("-");
  return (language && region && region.length === 2) ? `${language.toLowerCase()}-${region.toUpperCase()}` : "";
};

/**
 * Picks the UI locale for the browser preferences (in order) or returns the fallback.
 * Per preference:
 *   1. Multilingual market (BE, CH) and an official language of the market -> joined with the
 *      market country (fr-FR on .be -> fr-BE, nl-NL on .be -> nl-BE)
 *   2. same language as the fallback (domain) locale -> the fallback locale
 *   3. the exact regional browser locale, if the application supports it (nl-BE)
 *   4. language + its own country, if the application supports it (de -> de-DE)
 *   5. first supported locale of that language (en-US -> en-GB, sv -> sv-SE)
 * Nothing matched -> fallback.
 */
export const matchDisplayLocale = (preferences, ctryCode, fallbackLocale) => {
  const marketLanguages = multiLingualCountries[ctryCode];
  const fallbackPrefix = getLangPrefix(fallbackLocale);

  for (const preference of (preferences || [])) {
    const prefix = typeof preference === "string" ? getLangPrefix(preference) : "";
    if (!prefix) continue;

    if (marketLanguages && marketLanguages.includes(prefix)) return `${prefix}-${ctryCode}`;
    if (prefix === fallbackPrefix) return fallbackLocale;

    const exact = normalizeRegionalLocale(preference);
    if (exact && eligibleDisplayLocales.includes(exact)) return exact;

    const ownCountry = `${prefix}-${prefix.toUpperCase()}`;
    if (eligibleDisplayLocales.includes(ownCountry)) return ownCountry;

    const sameLanguage = eligibleDisplayLocales.find(locale => getLangPrefix(locale) === prefix);
    if (sameLanguage) return sameLanguage;
  }
  return fallbackLocale;
};

/**
 * Single source of truth for language and country.
 *
 * DATA language (`locale`: payloads, B2C `language`, persistence, emails):
 * - Single-language domain (UK, DE, FR, IT, ES, Nordic...):
 *     strictly domain default (QS and browser settings ignored).
 * - Multilingual domain (BE, CH):
 *     1. Valid QS (both params, country matches domain, language supported by market) -> QS language.
 *     2. Browser settings matched against market supported languages -> matched market locale.
 *     3. No match -> domain default.
 * - QS country "uk" is treated as "GB".
 *
 * DISPLAY language (`displayLocale`: UI translations, <html lang>, landing-page features,
 * offers-checkbox link), decided by the launch URL:
 * - `language` AND a `country` that matches the domain (MobileConnect / native app journeys) ->
 *     the user's preferred app language matched against the supported UI locales
 *     (e.g. "en" -> "en-GB", "it" -> "it-IT"), regardless of domain type. Falls back to `locale` if
 *     no supported UI translation exists for that language (e.g. "ja" on .be -> "nl-BE").
 *     Remembered for the tab so later page loads without a query string (B2C return, redirects,
 *     refresh) keep it.
 * - Any other `language` / `country` combination (language only, country only, `country` that does
 *     not match the domain) -> same as `locale`, because the query string is not trusted.
 * - Neither parameter and no remembered launch language (web sign-up journey) ->
 *     the browser language matched against the supported locales, falling back to `locale`.
 * - Domains fi, se, dk (Finland, Sweden, Denmark): always `locale` (the domain language), whatever
 *     the browser or any saved launch language.
 * The display language never changes `locale` or anything persisted in local storage.
 *
 * Computed and written once per page load; later calls return the same result.
 * @returns {{ locale: string, ctryCode: string, displayLocale: string }}
 */
export const resolveAppLanguage = () => {
  if (resolvedLanguage) {
    return resolvedLanguage;
  }

  const domainCountryCode = extractCountryCodeFromDomain();
  const ctryCode = (domainCountryCode === "uk") ? "GB" : domainCountryCode.toUpperCase();
  const domainDefault = languageCodes[domainCountryCode] || "en-GB";
  const isMultiLingual = Object.prototype.hasOwnProperty.call(multiLingualCountries, ctryCode);

  const urlParams = new URLSearchParams(window.location.search);
  const qsLanguage = urlParams.get("language");
  const qsCountry = urlParams.get("country");
  const qsCountryCode = qsCountry ? ((qsCountry.toUpperCase() === "UK") ? "GB" : qsCountry.toUpperCase()) : null;
  const isQSApplicable = Boolean(
    qsLanguage
    && qsCountryCode === ctryCode
    && isMultiLingual
    && (multiLingualCountries[ctryCode] || []).includes(getLangPrefix(qsLanguage))
  );

  const browserLanguage = navigator.language || navigator.userLanguage;

  const locale = isMultiLingual
    ? resolveMultiLingual(ctryCode, domainDefault, qsLanguage, isQSApplicable, browserLanguage)
    : resolveSingleDomain(domainDefault);

  const browserPreferences = (navigator.languages && navigator.languages.length > 0)
    ? navigator.languages
    : [browserLanguage];
  let displayLocale;
  if (DOMAIN_ONLY_DISPLAY_COUNTRIES.includes(domainCountryCode)) {
    displayLocale = locale;
  } else if (qsLanguage || qsCountry) {
    const isQSCountryMatchingDomain = qsCountryCode === ctryCode;
    displayLocale = (qsLanguage && isQSCountryMatchingDomain)
      ? matchDisplayLocale([qsLanguage], ctryCode, locale)
      : locale;
    saveLaunchDisplayLocale(displayLocale);
  } else {
    displayLocale =  readLaunchDisplayLocale() || matchDisplayLocale(browserPreferences, ctryCode, locale);
  }

  setLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROCTRYCODE, ctryCode);
  setLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLANGUAGECODE, locale);

  resolvedLanguage = { locale, ctryCode, displayLocale };
  return resolvedLanguage;
};

/**
 * Language the UI is displayed in (see resolveAppLanguage). Never use it for payloads,
 * persistence or B2C `language`: those follow getLangCode()/getLanguagePrefix().
 * @returns {string} locale, e.g. "fr-BE"
 */
export const getDisplayLangCode = () => resolveAppLanguage().displayLocale;

/**
 * Method to fetch the language code from local storage.
 * Logout clears ECOPROLANGUAGECODE and nothing rewrites it until the next page load, so fall back
 * to the language already resolved for this page load (cached, read-only, no storage write).
 * @returns string
 */
export const getLangCode = () => {
    return getLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLANGUAGECODE) || resolveAppLanguage().locale;
}

/**
 * Returns the 2-letter ISO language prefix (e.g. "en", "fr", "nl", "de").
 * Null and undefined safe.
 * @returns string
 */
export const getLanguagePrefix = () => {
    const langCode = getLangCode();
    return (typeof langCode === "string" && langCode.length > 0)
        ? langCode.split("-")[0].toLowerCase()
        : "en";
}

/**
 * Method to set the language code in local storage
 * @param {string} langCode
 * @returns void
 */
export const setLangCode = (langCode, type=null) => {
     if(langCode){
        if(type && type === USER_LANGUAGE_CODE_TYPE){
            let domainCountry = extractCountryCodeFromDomain();
            if(domainCountry.toLowerCase() === "uk"){
                domainCountry = "GB"
            }
            const language_code = `${langCode}-${domainCountry.toUpperCase()}`;
            setLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLANGUAGECODE, language_code);
        } else {
            setLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLANGUAGECODE, langCode);
        }
    } else {
        const domainCountry = extractCountryCodeFromDomain();
        setLangCode(languageCodes[domainCountry]);
    }
}
export const removeLangCode = () => {
    removeLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLANGUAGECODE);
}
/**
 * Method to get the country code
 * @returns void
 */
export const getCountryCode = () => {
    return getLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROCTRYCODE);
}

export const extractCountryCodeFromDomain = () => {
    const appDomain = window.location.host;
    const splittedDomain = appDomain.split(".");
    const matchedCountryCode = splittedDomain.filter(element => Object.keys(languageCodes).includes(element));
    return (matchedCountryCode.length > 0) ? matchedCountryCode.toString() : "uk";
}
/**
 * Method to get currency code based on country code
 * @param {string} countryCode 
 * @returns string
 */
export const getCurrencyCode =(countryCode) =>{
    const countries = ENVSettings.COUNTRIES
    let countryObject = countries.find(item => item.code === countryCode)
    return countryObject[`currency-code`];
}
/**
 * Method to get country code by country name
 * @param {string} countryName 
 * @returns string
 */
export const extractCountryCode = (countryName)  => {
    const countries = ENVSettings.COUNTRIES
    const extractedCode = countries.filter((value) => {
      return value.name ===  countryName
    })
    return (extractedCode.length > 0 ) ? extractedCode[0].code : "";
}
/**
 * Method to get country name by country code
 * @param {string} countryCode
 * @returns string
 */
export const extractCountryName = (countryCode)  => {
    const countries = ENVSettings.COUNTRIES
    const extractedCode = countries.filter((value) => {
      return value.code2 ===  countryCode
    })
    return (extractedCode.length > 0 ) ? extractedCode[0].name : "";
}
